import React from 'react';
import { Box, Typography, IconButton, CircularProgress } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { VisitaEstadoChip } from '../../components/EstadoChip';
import { VisitaEstado } from '../../consts/visitasStatus';
import { useTranslation } from 'react-i18next';
import { usePatchVerosimilitudDataMutation } from '../../../../services/visitasApi';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../../../hooks/reduxHooks';
import { showAlert } from '../../../../features/slices/applicationSlice';
import { useSnackbar } from 'notistack';
import { useDeleteVerosimilitudVisitMutation } from '../../../../services/visitasApi';
import { Alert } from '@mui/material';
import dayjs from 'dayjs';

interface VerosimilitudSectionProps {
    isVerosimilitudDraft: boolean;
    isVerosimilitudPendienteSinRespuestas: boolean;
    selectedVisita: any;
    shouldShowVerosimilitudRow: boolean;
    hasRgrlPresentations: boolean;
    loadingVerosimilitud?: boolean;
    loadingRgrl?: boolean;
    handleOpenVerosimilitud: (options?: { mappedResponses?: any[] }) => void;
}

const VerosimilitudSection: React.FC<VerosimilitudSectionProps> = ({
    isVerosimilitudDraft,
    selectedVisita,
    handleOpenVerosimilitud,
    hasRgrlPresentations,
    loadingVerosimilitud,
    loadingRgrl
}) => {
    const { t } = useTranslation('visitas');
    const [patchVerosimilitudData] = usePatchVerosimilitudDataMutation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [deleteVerosimilitudVisit] = useDeleteVerosimilitudVisitMutation();

    const handleCreateDraft = () => {
        handleOpenVerosimilitud();
    };

    const handleEditDraft = () => {
        const mappedResponses = mapVerosimilitudRespuestasToLocalStorageFormat(
            selectedVisita.verosimilitudRespuestas
        );

        handleOpenVerosimilitud({ mappedResponses });
    };
    const mapVerosimilitudRespuestasToLocalStorageFormat = (verosimilitudRespuestas: any[]) => {
        if (!Array.isArray(verosimilitudRespuestas)) return [];
    
        const sectionsMap: Record<number, any> = {};
    
        verosimilitudRespuestas.forEach((respuesta) => {
            const idSeccion = respuesta.id_seccion;
            const idRespuesta = respuesta.id_tipo_respuesta;
            const fechaRegulacion = respuesta.fecha_regulacion;
    
            if (!sectionsMap[idSeccion]) {
                sectionsMap[idSeccion] = {
                    idSeccion,
                    opciones: [],
                    fechaRegulacion: '' 
                };
            }
    
            sectionsMap[idSeccion].opciones.push({
                id: idRespuesta,
                selected: true
            });
    
            if (idRespuesta === 2 && fechaRegulacion) {
                sectionsMap[idSeccion].fechaRegulacion = dayjs(fechaRegulacion).format('YYYY-MM-DD');
            }
        });
    
        const finalSections = Object.values(sectionsMap).map((section: any) => {
            const posiblesOpciones = [1, 2, 3];
            posiblesOpciones.forEach((opcionId) => {
                if (!section.opciones.find((o: any) => o.id === opcionId)) {
                    section.opciones.push({
                        id: opcionId,
                        selected: false
                    });
                }
            });
            section.opciones.sort((a: any, b: any) => a.id - b.id);
    
            return {
                idSeccion: section.idSeccion,
                opciones: section.opciones,
                fechaRegulacion: section.fechaRegulacion
            };
        });
    
        return finalSections;
    };
    
    

    const handleDeleteFinalizada = () => {
        dispatch(
            showAlert({
                icon: 'info',
                title: t('alerts.deleteVerosimilitudFinalizada.title'),
                message: t('alerts.deleteVerosimilitudFinalizada.message'),
                confirmText: t('alerts.deleteVerosimilitudFinalizada.confirmText'),
                cancelText: t('alerts.deleteVerosimilitudFinalizada.cancelText'),
                itemData: true,
                confirmAction: confirmDeleteFinalizada
            })
        );
    };

    const confirmDeleteFinalizada = async () => {
        const idVisita = selectedVisita?.idVisita;
        const idPresentacion = selectedVisita?.idPresentacion;

        if (!idVisita || !idPresentacion) {
            enqueueSnackbar(t('alerts.deleteVerosimilitudFinalizada.errorData'), {
                variant: 'error'
            });
            return;
        }

        try {
            await deleteVerosimilitudVisit({ idVisita, idPresentacion }).unwrap();
            enqueueSnackbar(t('alerts.deleteVerosimilitudFinalizada.success'), {
                variant: 'success'
            });
        } catch (error) {
            enqueueSnackbar(t('alerts.deleteVerosimilitudFinalizada.error'), { variant: 'error' });
        }
    };

    return (
        <><Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            sx={{
                borderBottom: '1px solid #ccc',
                padding: '12px 8px',
            }}
        >

            <Box display="flex" alignItems="center" gap={2}>
            <Typography>{t('verosimilitud.title')}</Typography>

            {loadingVerosimilitud ? (
                <CircularProgress size={24} />
            ) : (
                <>
                <VisitaEstadoChip
                    estadoId={isVerosimilitudDraft ? VisitaEstado.REALIZADA : selectedVisita.idEstado}
                />
               
                </>
            )}
            </Box>



            <Box display="flex" gap={1}>
                {isVerosimilitudDraft ? (
                    <>
                        <IconButton onClick={handleDeleteFinalizada}>
                            <DeleteIcon />
                        </IconButton>
                        {hasRgrlPresentations && (
                            <IconButton  onClick={handleEditDraft}>
                            <EditIcon  />
                        </IconButton>
                        )}
                        
                    </>
                ) : (
                    <>
                    {hasRgrlPresentations && (
                    <IconButton onClick={handleCreateDraft}>
                        <EditIcon />
                    </IconButton>
                    )}
                    </>
                )}
                
            </Box>


        </Box>
        <Box width="100%" sx={{ mt: 2 }}>
            {loadingRgrl ? (
                <CircularProgress size={24} />
            ) : (
                <>
                    {isVerosimilitudDraft ? (
                        <Alert severity="success" sx={{ mt: 1 }}>
                            {t('verosimilitud.alerts.verosimilitudComplete')}
                        </Alert>
                    ) : (
                        <>
                            {!hasRgrlPresentations && (
                                <Alert severity="error" sx={{ mt: 1 }}>
                                    {t('verosimilitud.alerts.noPresentations')}
                                </Alert>
                            )}
                            {hasRgrlPresentations && (
                                <Alert severity="success" sx={{ mt: 1 }}>
                                    {t('verosimilitud.alerts.presentationsExist')}
                                </Alert>
                            )}
                        </>
                    )}
                </>
            )}
        </Box>



        </>
    );
};

export default VerosimilitudSection;
